import { Injectable, NgZone, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import {
  Observable,
  Subject,
  catchError,
  finalize,
  of,
  shareReplay,
  takeUntil,
  tap,
  throwError,
} from 'rxjs';
import { LoginResponseDto, UserDto } from 'sham-components/shared/models/auth';
import {
  clearDashboardSessionStorage,
  verifyDashboardSession,
} from 'sham-components/services';
import { SigninApi } from './signin-api';
import { API_BASE_URL } from '@/environments/environment';
import { HttpClient } from '@angular/common/http';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly signinApi = inject(SigninApi);
  private readonly zone = inject(NgZone);
  private readonly router = inject(Router);
  http = inject(HttpClient);
  private readonly isBrowser = typeof window !== 'undefined';
  private refreshTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private refreshRequest$: Observable<LoginResponseDto> | null = null;
  private readonly logout$ = new Subject<void>();

  readonly isLoggedIn = signal(false);

  processRefreshVerification(): Observable<LoginResponseDto | boolean | null> {
    if (!this.isBrowser) {
      return of(null);
    }

    return verifyDashboardSession({
      refresh: () => this.refreshToken(),
      onValidAccess: () => {
        this.isLoggedIn.set(true);
        this.queueSilentRefresh();
      },
      clear: () => this.clearSession(),
    });
  }

  saveLogin(response: LoginResponseDto, rememberMe: boolean) {
    if (!this.isBrowser) {
      return;
    }

    sessionStorage.setItem('accessToken', response.accessToken);
    sessionStorage.setItem('user', JSON.stringify(response.user));
    sessionStorage.setItem('accessTokenExpiresAt', response.accessTokenExpiresAtUtc);

    localStorage.removeItem('refreshToken');
    sessionStorage.removeItem('refreshToken');
    if (response.refreshToken) {
      (rememberMe ? localStorage : sessionStorage).setItem(
        'refreshToken',
        response.refreshToken,
      );
    }

    this.isLoggedIn.set(true);
    this.queueSilentRefresh();
  }

  getUser(): UserDto | null {
    if (!this.isBrowser) {
      return null;
    }

    const user = sessionStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  }

  handleLoginRedirection(_user: UserDto) {
    void this.router.navigate(['/app']);
  }

  logout() {
    this.logout$.next();

    this.clearSession();
    void this.router.navigate(['/login']);
  }

  private clearSession() {
    if (!this.isBrowser) return;
    if (this.refreshTimeoutId) clearTimeout(this.refreshTimeoutId);
    this.refreshTimeoutId = null;
    clearDashboardSessionStorage();
    this.isLoggedIn.set(false);
  }

  refreshToken(): Observable<LoginResponseDto> {
    if (this.refreshRequest$) {
      return this.refreshRequest$;
    }

    const refreshToken = this.getRefreshToken();

    if (!refreshToken) {
      this.logout();
      return throwError(() => new Error('No refresh token'));
    }

    const rememberMe = localStorage.getItem('refreshToken') !== null;
    this.refreshRequest$ = this.signinApi.refreshToken({ refreshToken }).pipe(
      takeUntil(this.logout$),
      tap((response) => this.saveLogin(response, rememberMe)),
      catchError((error: unknown) => {
        this.clearSession();
        return throwError(() => error);
      }),
      finalize(() => (this.refreshRequest$ = null)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    return this.refreshRequest$;
  }

  private queueSilentRefresh() {
    if (!this.isBrowser) {
      return;
    }

    if (this.refreshTimeoutId) {
      clearTimeout(this.refreshTimeoutId);
    }

    const expiresAt = sessionStorage.getItem('accessTokenExpiresAt');
    const refreshToken = this.getRefreshToken();

    if (!expiresAt || !refreshToken) {
      return;
    }

    const delay = new Date(expiresAt).getTime() - Date.now() - 2 * 60 * 1000;

    if (delay <= 0) {
      this.triggerSilentRefresh();
      return;
    }

    this.zone.runOutsideAngular(() => {
      this.refreshTimeoutId = setTimeout(() => {
        this.zone.run(() => this.triggerSilentRefresh());
      }, delay);
    });
  }

  private triggerSilentRefresh() {
    this.refreshToken().subscribe({ error: () => undefined });
  }

  private getRefreshToken() {
    return (
      localStorage.getItem('refreshToken') ?? sessionStorage.getItem('refreshToken')
    );
  }

  getMe(): Observable<AccountSummary> {
    return this.http.get<AccountSummary>(`${API_BASE_URL}/api/v1/account/me`);
  }
}

export interface AccountSummary {
  userId: string;
  fullName: string | null;
  userName: string;
  email: string;
  profilePhotoUrl: string | null;
  roles: string[];
}
